// src/components/audit/AuditRowDetail.jsx
import React, { useState } from "react";
import axios from "axios";
import CommentThread from "./CommentThread";
import "./Comment.css";

export default function AuditRowDetail({ row, onEdit, onToast, user, onRefresh }) {
  const [needsHelp, setNeedsHelp] = useState(!!row.needs_help);

  const toggleHelp = async () => {
    try {
      const res = await axios.put(
        "/api/audit.php",
        { finding_id: row.finding_id, needs_help: needsHelp ? 0 : 1 },
        { withCredentials: true }
      );
      if (res.data?.success) {
        setNeedsHelp(!needsHelp);
        onToast?.(needsHelp ? "Help flag removed" : "Help requested", "success");
        onRefresh?.({ preserveExpanded: true });
      } else {
        onToast?.("Failed to update help status", "danger");
      }
    } catch {
      onToast?.("Error updating help flag", "danger");
    }
  };

  const handleShare = () => {
    const subject = encodeURIComponent(`Audit Finding ${row.finding_id}`);
    const body = encodeURIComponent(
      `Hi,\n\nPlease find below the details of finding ${row.finding_id}.\n\n` +
        `Theme: ${row.theme || "—"}\n` +
        `Severity: ${row.severity || "—"}\n` +
        `Status: ${row.status || "—"}\n` +
        `Country: ${row.country || "—"}\n` +
        `Year: ${row.year || "—"}\n` +
        (row.is_subsidiary ? `Subsidiary: ${row.subsidiary_name || "Yes"}\n` : "") +
        `\nDescription:\n${row.finding || "No description provided."}\n\n` +
        `Planned Remediation:\n${row.planned_remediation || "No remediation provided."}\n\n` +
        `Regards,\n${user?.displayName || user?.name || "Security Team"}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="audit-details-card">
      <div className="audit-details-header">
        <div className="audit-id-display">
          <strong>{row.finding_id}</strong>
        </div>

        <div className="audit-pills">
          {row.previously_identified && (
            <span className="audit-badge badge-soft-primary">
              Previously: {row.previously_identified}
            </span>
          )}

          <button
            type="button"
            className="audit-add-btn"
            onClick={() => onEdit && onEdit(row)}
            title="Edit finding"
          >
            <i className="bi bi-pencil" style={{ marginRight: 4 }} />
            Edit
          </button>

          <button
            type="button"
            className="audit-add-btn"
            onClick={handleShare}
            title="Share via email"
            style={{ marginLeft: 4 }}
          >
            <i className="bi bi-envelope" style={{ marginRight: 4 }} />
            Share
          </button>

          <button
            type="button"
            className={`audit-add-btn ${needsHelp ? "btn-help-active" : ""}`}
            onClick={toggleHelp}
            title="Ask for help"
            style={{ marginLeft: 4 }}
          >
            <i
              className={`bi bi-flag${needsHelp ? "-fill" : ""}`}
              style={{ marginRight: 4, color: needsHelp ? "#c77a3a" : "#f8f9fa" }}
            />
            {needsHelp ? "Help requested" : "Ask for help"}
          </button>
        </div>
      </div>

      <div className="audit-details-section">
        <div className="audit-section-title">Description</div>
        <div className="audit-section-body">
          {row.finding || "No description provided."}
        </div>
      </div>

      <div className="audit-details-section">
        <div className="audit-section-title">Planned Remediation</div>
        <div className="audit-section-body">
          {row.planned_remediation || "No remediation provided."}
        </div>
      </div>

      <div className="audit-meta-grid">
        <div className="meta-item">
          <div className="meta-label">Theme</div>
          <div className="meta-value">{row.theme || "—"}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Severity</div>
          <div className="meta-value">{row.severity || "—"}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Owner(s)</div>
          <div className="meta-value">{row.owners || "—"}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Country</div>
          <div className="meta-value">{row.country || "—"}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Year</div>
          <div className="meta-value">{row.year || "—"}</div>
        </div>

        {/* ✅ Show subsidiary info right after Year when true */}
        {row.is_subsidiary ? (
          <div className="meta-item">
            <div className="meta-label">Subsidiary</div>
            <div className="meta-value">{row.subsidiary_name || "Yes"}</div>
          </div>
        ) : null}
      </div>

      <div className="audit-details-section" style={{ marginTop: "1rem" }}>
        <CommentThread
          findingId={row.finding_id}
          onToast={onToast}
          currentUser={user}
          onRefresh={onRefresh}
        />
      </div>
    </div>
  );
}

function getBadgeVariant(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("resolved")) return "success";
  if (s.includes("progress")) return "info";
  if (s.includes("pending")) return "warning";
  if (s.includes("open")) return "secondary";
  return "secondary";
}
